import React, { useState, useEffect } from 'react';
import { Typography, Card, CardHeader, CardContent, Avatar,
   Grid, TextField, Button, Box, Chip, Paper,
  Divider, FormControl, InputLabel, Select, MenuItem, Stack,
   Checkbox, InputAdornment, ButtonGroup,
   List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, ListItemAvatar, CardActions,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   LinearProgress,
   useMediaQuery,
   useTheme,
   Collapse,
   Hidden,
   Snackbar,
   Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import MonitorIcon from '@mui/icons-material/Monitor';
import FilterListIcon from '@mui/icons-material/FilterList';
import StyledLinearProgress from '../helpers/StyledLinearProgress';
import { useNavigate } from 'react-router-dom';
import MapChart from '../cloudmanagement/MapChart';
import ServerCard from '../cloudmanagement/ServerCard';
import ServerTableRow from '../cloudmanagement/ServerTableRow';
import AddNodeForm from '../cloudmanagement/AddNodeForm';
import { 
  ToggleButton, 
  ToggleButtonGroup 
} from '@mui/material';
import {
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  PowerOff as PowerOffIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  getNodes,
  getNodeById, 
  deleteNode, 
  transformNodeToDisplayFormat,
  NodeType,
  NodeStatus 
} from '../api/services/computeService/NodeService';
import { useKeycloak } from '@react-keycloak/web';

const geoUrl = require('../cloudmanagement/world-countries.json');

export default function CloudManagement() {
  const [showAddNodeForm, setShowAddNodeForm] = useState(false);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServers, setSelectedServers] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    location: 'all',
    os: 'all',
    type: 'all'
  });
  const [viewType, setViewType] = useState('tiles');
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [deletingNodes, setDeletingNodes] = useState(new Set());
  const [addingNodes, setAddingNodes] = useState(new Set());
  const [pendingOperations, setPendingOperations] = useState(new Set());
  const { keycloak } = useKeycloak();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const initializeServers = async () => {
      if (!keycloak.token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        await keycloak.updateToken(30);
        
        const response = await getNodes({
          paginationParams: { page: 0, page_size: 50 },
          token: keycloak.token
        });
        
        if (response.success && response.data.nodes && response.data.nodes.length > 0) {
          const nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
          
          if (nodeIds.length === 0) {
            setError('No valid node IDs found');
            setServers([createFallbackServer()]);
            return;
          }

          const detailedNodes = await Promise.all(
            nodeIds.map(async (nodeId) => {
              try {
                const nodeResponse = await getNodeById(nodeId, keycloak.token);
                if (nodeResponse.success && nodeResponse.data) {
                  return transformNodeToDisplayFormat(nodeResponse.data);
                }
                return null;
              } catch (error) {
                console.warn(`Failed to fetch details for node ${nodeId}:`, error);
                return null;
              }
            })
          );

          const validNodes = detailedNodes.filter(node => node !== null);
          
          if (validNodes.length === 0) {
            setError('Failed to fetch detailed information for any nodes');
            setServers([createFallbackServer()]);
            return;
          }

          const enhancedNodes = await Promise.all(
            validNodes.map(node => enhanceNodeWithGeoData(node))
          );
          
          setServers(enhancedNodes);
          
          const hasRegisteringNodes = enhancedNodes.some(node => node.isRegistering);
          if (hasRegisteringNodes && !autoRefreshEnabled) {
            setAutoRefreshEnabled(true);
            setRefreshMessage('Auto-refresh enabled: Some nodes are still being set up');
          } else if (!hasRegisteringNodes && autoRefreshEnabled) {
            setAutoRefreshEnabled(false);
            setRefreshMessage('');
          }
        } else {
          console.log('GetNodes failed or returned empty, trying fallback node by ID...');
        }
      } catch (error) {
        console.error('Error initializing servers:', error);
        console.log('Primary fetch failed, trying fallback node by ID...');
        
        try {
          await keycloak.updateToken(30);
          const fallbackResponse = await getNodeById('c11e96f5-a91b-44ff-9fb6-7c29cb8ba383', keycloak.token);
          
          if (fallbackResponse.success && fallbackResponse.data) {
            const transformedNode = transformNodeToDisplayFormat(fallbackResponse.data);
            if (transformedNode) {
              const enhancedNode = await enhanceNodeWithGeoData(transformedNode);
              setServers([enhancedNode]);
              console.log('Successfully loaded fallback node after primary error');
            } else {
              setError(error.message || 'An unexpected error occurred');
              setServers([createFallbackServer()]);
            }
          } else {
            setError(error.message || 'An unexpected error occurred');
            setServers([createFallbackServer()]);
          }
        } catch (fallbackError) {
          console.error('Fallback node fetch failed after primary error:', fallbackError);
          setError(error.message || 'An unexpected error occurred');
          setServers([createFallbackServer()]);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeServers();
  }, [keycloak.token]);

  useEffect(() => {
    let intervalId;
    
    if (autoRefreshEnabled || pendingOperations.size > 0) {
      intervalId = setInterval(async () => {
        if (autoRefreshEnabled) {
          console.log('Auto-refreshing due to registering nodes...');
        }
        if (pendingOperations.size > 0) {
          console.log('Auto-refreshing due to pending operations...');
        }
        await handleRefresh(true);
      }, 10000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, pendingOperations.size, keycloak.token]);

  const enhanceNodeWithGeoData = async (node) => {
    const countryCode = getCountryFromRegion(node.region);
    const countryData = geoUrl.features.find(feature => feature.id === countryCode);
    let coordinates = [0, 0];
    
    if (countryData && countryData.geometry) {
      if (countryData.geometry.type === 'Polygon') {
        const coords = countryData.geometry.coordinates[0];
        coordinates = coords.reduce((acc, curr) => [acc[0] + curr[0], acc[1] + curr[1]], [0, 0])
          .map(sum => sum / coords.length);
      } else if (countryData.geometry.type === 'MultiPolygon') {
        const coords = countryData.geometry.coordinates[0][0];
        coordinates = coords.reduce((acc, curr) => [acc[0] + curr[0], acc[1] + curr[1]], [0, 0])
          .map(sum => sum / coords.length);
      }
    }

    return {
      ...node,
      coordinates
    };
  };

  const getCountryFromRegion = (region) => {
    const regionMap = {
      'us-east-1': 'USA',
      'us-west-1': 'USA', 
      'us-west-2': 'USA',
      'eu-west-1': 'UK',
      'eu-west-2': 'UK',
      'eu-central-1': 'DEU',
      'ap-south-1': 'IND',
      'ap-southeast-1': 'SGP',
      'ap-northeast-1': 'JPN'
    };
    return regionMap[region] || 'USA';
  };

  const createFallbackServer = () => ({
    id: "fallback-server-001",
    nodeId: "fallback-server-001",
    hostname: "Demo Server Node",
    location: "us-east-1", 
    coordinates: [-95.7129, 37.0902],
    status: "Online",
    type: "High Performance",
    machineId: "fallback-server-001",
    ipAddress: "192.168.1.100",
    port: "22",
    power: "N/A",
    specification: "16 CPUs, 128GB RAM",
    function: "Demo Node",
    gpuType: "RTX",
    gpu: "NVIDIA",
    numGpus: 4,
    gpus: 4,
    mem: 32,
    vram: 32,
    health: "Good",
    os: "Ubuntu",
    osVersion: "20.04",
    kernelVersion: "5.4.0",
    singleNode: true,
    storage: 1000,
    region: "us-east-1",
    isRegistering: false
  });

  if (loading) {
    return (
      <Box sx={{ padding: { xs: 2, sm: 3 } }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading servers...</Typography>
      </Box>
    );
  }

  const handleAddServer = () => {
    setShowAddNodeForm(true);
  };

  const handleAddNodeSuccess = async (nodeData) => {
    console.log('Node added successfully:', nodeData);
    
    // Generate a temporary ID for tracking if nodeData doesn't have an ID
    const nodeId = nodeData.id || nodeData.nodeId || `temp-${Date.now()}`;
    
    // Mark node as being added
    setAddingNodes(prev => new Set([...prev, nodeId]));
    setPendingOperations(prev => new Set([...prev, nodeId]));
    setRefreshMessage('Node addition in progress. Auto-refreshing until it appears...');
    
    // Start auto-refresh loop to check when node appears
    const checkAddition = setInterval(async () => {
      try {
        await keycloak.updateToken(30);
        const refreshResponse = await getNodes({
          paginationParams: { page: 0, page_size: 50 },
          token: keycloak.token
        });
        
        if (refreshResponse.success && refreshResponse.data.nodes) {
          const nodeIds = refreshResponse.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
          let nodeAppeared = false;
          
          // Check if we can find the node (either by ID or by hostname if provided)
          if (nodeData.hostname) {
            // Try to find by hostname in the detailed node data
            const detailedNodes = await Promise.all(
              nodeIds.map(async (nodeId) => {
                try {
                  const nodeResponse = await getNodeById(nodeId, keycloak.token);
                  if (nodeResponse.success && nodeResponse.data) {
                    return transformNodeToDisplayFormat(nodeResponse.data);
                  }
                  return null;
                } catch (error) {
                  console.warn(`Failed to fetch details for node ${nodeId}:`, error);
                  return null;
                }
              })
            );
            
            const validNodes = detailedNodes.filter(node => node !== null);
            nodeAppeared = validNodes.some(node => 
              node.hostname === nodeData.hostname || 
              node.id === nodeId ||
              node.nodeId === nodeId
            );
          } else {
            // Check by ID only
            nodeAppeared = nodeIds.includes(nodeId);
          }
          
          if (nodeAppeared) {
            // Node appeared, stop the refresh loop
            clearInterval(checkAddition);
            setAddingNodes(prev => {
              const newSet = new Set(prev);
              newSet.delete(nodeId);
              return newSet;
            });
            setPendingOperations(prev => {
              const newSet = new Set(prev);
              newSet.delete(nodeId);
              return newSet;
            });
            setRefreshMessage('Node successfully added!');
            setTimeout(() => setRefreshMessage(''), 3000);
            
            // Do a final refresh to update the UI
            await handleRefresh(true);
          } else {
            // Node hasn't appeared yet, continue checking
            await handleRefresh(true);
          }
        }
      } catch (error) {
        console.error('Error checking node addition:', error);
        // Continue trying, don't stop the interval
      }
    }, 3000); // Check every 3 seconds

    // Set a timeout to stop checking after 3 minutes
    setTimeout(() => {
      clearInterval(checkAddition);
      setAddingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
      setError('Node addition timeout. Please refresh manually to check if the node appeared.');
    }, 180000); // 3 minutes timeout
  };

  const handleRefresh = async (isAutoRefresh = false) => {
    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    if (!isAutoRefresh) {
      setLoading(true);
    }
    
    try {
      await keycloak.updateToken(30);
      
      const response = await getNodes({
        paginationParams: { page: 0, page_size: 50 },
        token: keycloak.token
      });
      
      if (response.success && response.data.nodes && response.data.nodes.length > 0) {
        const nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
        
        if (nodeIds.length === 0) {
          setError('No valid node IDs found');
          setServers([createFallbackServer()]);
          return;
        }

        const detailedNodes = await Promise.all(
          nodeIds.map(async (nodeId) => {
            try {
              const nodeResponse = await getNodeById(nodeId, keycloak.token);
              if (nodeResponse.success && nodeResponse.data) {
                return transformNodeToDisplayFormat(nodeResponse.data);
              }
              return null;
            } catch (error) {
              console.warn(`Failed to fetch details for node ${nodeId}:`, error);
              return null;
            }
          })
        );

        const validNodes = detailedNodes.filter(node => node !== null);
        
        if (validNodes.length === 0) {
          setError('Failed to fetch detailed information for any nodes');
          setServers([createFallbackServer()]);
          return;
        }

        const enhancedNodes = await Promise.all(
          validNodes.map(node => enhanceNodeWithGeoData(node))
        );
        
        setServers(enhancedNodes);
        setError(null);
        
        // Clean up pending operations for nodes that no longer exist
        const currentNodeIds = new Set(enhancedNodes.map(node => node.id));
        setDeletingNodes(prev => {
          const newSet = new Set();
          for (const nodeId of prev) {
            if (currentNodeIds.has(nodeId)) {
              newSet.add(nodeId);
            }
          }
          return newSet;
        });
        setPendingOperations(prev => {
          const newSet = new Set();
          for (const nodeId of prev) {
            if (currentNodeIds.has(nodeId) || addingNodes.has(nodeId)) {
              newSet.add(nodeId);
            }
          }
          return newSet;
        });
        
        const hasRegisteringNodes = enhancedNodes.some(node => node.isRegistering);
        if (hasRegisteringNodes && !autoRefreshEnabled) {
          setAutoRefreshEnabled(true);
          if (!isAutoRefresh) {
            setRefreshMessage('Auto-refresh enabled: Some nodes are still being set up');
          }
        } else if (!hasRegisteringNodes && autoRefreshEnabled) {
          setAutoRefreshEnabled(false);
          setRefreshMessage('All nodes are now active');
          setTimeout(() => setRefreshMessage(''), 3000);
        }
      } else {
        setError(response.error || 'Failed to refresh nodes');
        setServers([createFallbackServer()]);
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      setError('Failed to refresh server data');
      setServers([createFallbackServer()]);
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  };

  const handleDeleteServer = async (serverId) => {
    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    try {
      // Mark node as deleting
      setDeletingNodes(prev => new Set([...prev, serverId]));
      setPendingOperations(prev => new Set([...prev, serverId]));
      
      await keycloak.updateToken(30);
      
      const response = await deleteNode(serverId, keycloak.token);
      
      if (response.success) {
        console.log('Node deletion initiated successfully:', serverId);
        setRefreshMessage('Node deletion in progress. Auto-refreshing until completed...');
        
        // Start auto-refresh loop to check when node is actually gone
        const checkDeletion = setInterval(async () => {
          try {
            await keycloak.updateToken(30);
            const refreshResponse = await getNodes({
              paginationParams: { page: 0, page_size: 50 },
              token: keycloak.token
            });
            
            if (refreshResponse.success && refreshResponse.data.nodes) {
              const nodeIds = refreshResponse.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
              const nodeStillExists = nodeIds.includes(serverId);
              
              if (!nodeStillExists) {
                // Node is actually gone, stop the refresh loop
                clearInterval(checkDeletion);
                setDeletingNodes(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(serverId);
                  return newSet;
                });
                setPendingOperations(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(serverId);
                  return newSet;
                });
                setServers(prev => prev.filter(server => server.id !== serverId));
                setSelectedServers(prev => prev.filter(id => id !== serverId));
                setRefreshMessage('Node successfully deleted!');
                setTimeout(() => setRefreshMessage(''), 3000);
              } else {
                // Node still exists, update the full server list
                await handleRefresh(true);
              }
            }
          } catch (error) {
            console.error('Error checking node deletion:', error);
            // Continue trying, don't stop the interval
          }
        }, 3000); // Check every 3 seconds

        // Set a timeout to stop checking after 2 minutes
        setTimeout(() => {
          clearInterval(checkDeletion);
          setDeletingNodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(serverId);
            return newSet;
          });
          setPendingOperations(prev => {
            const newSet = new Set(prev);
            newSet.delete(serverId);
            return newSet;
          });
          setError('Node deletion timeout. Please refresh manually to check status.');
        }, 120000); // 2 minutes timeout
        
      } else {
        setDeletingNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(serverId);
          return newSet;
        });
        setPendingOperations(prev => {
          const newSet = new Set(prev);
          newSet.delete(serverId);
          return newSet;
        });
        setError(response.error || 'Failed to delete node');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      setDeletingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
      setError('Failed to delete server');
    }
  };

  const filteredServers = servers.filter(server => {
    const matchesSearch = server.hostname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         server.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         server.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filters.status === 'all' || 
                         server.status?.toLowerCase() === filters.status.toLowerCase();
    const matchesLocation = filters.location === 'all' || 
                           server.location?.toLowerCase().includes(filters.location.toLowerCase());
    const matchesOS = filters.os === 'all' || 
                     server.os?.toLowerCase().includes(filters.os.toLowerCase());
    const matchesType = filters.type === 'all' ||
                       server.type?.toLowerCase().includes(filters.type.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesLocation && matchesOS && matchesType;
  });

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on servers:`, selectedServers);
    if (action === 'delete') {
      const nonRegisteringServers = selectedServers.filter(serverId => {
        const server = servers.find(s => s.id === serverId);
        return server && !server.isRegistering && !deletingNodes.has(serverId);
      });
      
      if (nonRegisteringServers.length === 0) {
        setError('Cannot delete nodes that are currently being set up or are already being deleted');
        return;
      }
      
      const failedServers = selectedServers.filter(serverId => {
        const server = servers.find(s => s.id === serverId);
        return server && server.isRegistrationFailed;
      });
      
      if (failedServers.length > 0) {
        const confirmMessage = `You have selected ${failedServers.length} failed node(s) for deletion. Are you sure you want to proceed?`;
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }
      
      // Delete nodes one by one to track each individual deletion
      nonRegisteringServers.forEach(serverId => {
        handleDeleteServer(serverId);
      });
      
      // Clear selection after initiating deletions
      setSelectedServers([]);
    }
  };

  const handleServerSelect = (serverId) => {
    const server = servers.find(s => s.id === serverId);
    if (server && (server.isRegistering || deletingNodes.has(serverId) || pendingOperations.has(serverId))) {
      return;
    }
    
    setSelectedServers(prev => 
      prev.includes(serverId) 
        ? prev.filter(id => id !== serverId)
        : [...prev, serverId]
    );
  };

  const handleCloseRefreshMessage = () => {
    setRefreshMessage('');
  };

  const handleOpenConfigDialog = (server) => {
    setSelectedServer(server);
    setOpenConfigDialog(true);
  };

  const handleCloseConfigDialog = () => {
    setOpenConfigDialog(false);
    setSelectedServer(null);
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
      <Snackbar
        open={!!refreshMessage}
        onClose={handleCloseRefreshMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={handleCloseRefreshMessage} 
          severity={autoRefreshEnabled ? 'info' : 'success'} 
          sx={{ width: '100%' }}
        >
          {refreshMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        marginBottom: { xs: '16px', sm: '20px' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant={isSmall ? "h5" : "h4"} sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
          }}>
            Cloud Management
          </Typography>
          <Chip 
            label="Coming Soon" 
            size="small" 
            color="warning" 
            sx={{ 
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: '20px', sm: '24px' }
            }}
          />
          {autoRefreshEnabled && (
            <Chip 
              label="Auto-refreshing" 
              size="small" 
              color="info"
              icon={<LinearProgress sx={{ width: 12, height: 2 }} />}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: '20px', sm: '24px' }
              }}
            />
          )}
          {pendingOperations.size > 0 && (
            <Chip 
              label={`${pendingOperations.size} pending operation${pendingOperations.size > 1 ? 's' : ''}`} 
              size="small" 
              color="warning"
              icon={<LinearProgress sx={{ width: 12, height: 2 }} />}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: '20px', sm: '24px' }
              }}
            />
          )}
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1 }
        }}>
          <Button 
            variant="outlined" 
            onClick={handleRefresh}
            disabled={loading}
            size={isSmall ? "small" : "medium"}
            fullWidth={isSmall}
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1 }
            }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAddServer}
            size={isSmall ? "small" : "medium"}
            fullWidth={isSmall}
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1 }
            }}
          >
            Add Node
          </Button>
        </Box>
      </Box>

      {error && (
        <Paper sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: 2, 
          backgroundColor: 'error.dark', 
          color: 'error.contrastText' 
        }}>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Error: {error}
          </Typography>
        </Paper>
      )}

      <Paper 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          mt: { xs: 3, sm: 4 },
          mb: 2
        }}
      >
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid item xs={12} lg={5}>
            <TextField
              fullWidth
              placeholder="Search nodes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size={isSmall ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
          </Grid>
          
          {isMobile && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 0.75, sm: 1 }
                }}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Grid>
          )}
          
          <Grid item xs={12} lg={6}>
            <Collapse in={!isMobile || showFilters}>
              <Grid container spacing={{ xs: 1, sm: 1 }}>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size={isSmall ? "small" : "medium"}>
                    <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      Status
                    </InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      label="Status"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }
                      }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="online">Online</MenuItem>
                      <MenuItem value="offline">Offline</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size={isSmall ? "small" : "medium"}>
                    <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      Region
                    </InputLabel>
                    <Select
                      value={filters.location}
                      onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                      label="Region"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }
                      }}
                    >
                      <MenuItem value="all">All Regions</MenuItem>
                      <MenuItem value="us-east">US East</MenuItem>
                      <MenuItem value="us-west">US West</MenuItem>
                      <MenuItem value="eu-west">EU West</MenuItem>
                      <MenuItem value="ap-south">Asia Pacific</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size={isSmall ? "small" : "medium"}>
                    <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      OS
                    </InputLabel>
                    <Select
                      value={filters.os}
                      onChange={(e) => setFilters({ ...filters, os: e.target.value })}
                      label="OS"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }
                      }}
                    >
                      <MenuItem value="all">All OS</MenuItem>
                      <MenuItem value="ubuntu">Ubuntu</MenuItem>
                      <MenuItem value="windows">Windows</MenuItem>
                      <MenuItem value="centos">CentOS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size={isSmall ? "small" : "medium"}>
                    <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      Type
                    </InputLabel>
                    <Select
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                      label="Type"
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }
                      }}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="compute">Compute</MenuItem>
                      <MenuItem value="storage">Storage</MenuItem>
                      <MenuItem value="high performance">High Performance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
          
          <Grid item xs={12} lg={1}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', lg: 'flex-end' } }}>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, newView) => newView && setViewType(newView)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 }
                  }
                }}
              >
                <ToggleButton value="tiles" aria-label="grid view">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#1a1a1a',
          display: selectedServers.length > 0 ? 'flex' : 'none',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#fff',
              backgroundColor: 'primary.main',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
            }}
          >
            {selectedServers.length} selected
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            ⚠️ These actions will permanently affect the selected nodes
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => handleBulkAction('delete')}
            startIcon={<DeleteIcon />}
            size={isSmall ? "small" : "medium"}
            fullWidth={isSmall}
          >
            Delete Nodes
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 },
              backgroundColor: '#1a1a1a',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              height: { xs: '300px', sm: '400px' },
              width: '100%'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
              Node Locations
            </Typography>
            <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
              <MapChart servers={servers} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper 
            sx={{ 
              p: 2, 
              backgroundColor: '#1a1a1a', 
              borderRadius: '12px',
              height: { xs: '300px', sm: '400px' }
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
              Quick Overview
            </Typography>
            <Box sx={{ 
              mb: 3, 
              height: 'calc(100% - 40px)', 
              overflowY: 'auto'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                Active Nodes ({servers.length})
              </Typography>
              {servers.map((server) => (
                <Box 
                  key={server.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: server.status === 'Online' ? '#4caf50' : '#f44336',
                      mr: 1
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      {server.hostname}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      {server.region} • {server.type}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {viewType === 'tiles' ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {filteredServers.map((server) => (
            <Grid item xs={12} sm={6} lg={4} key={server.id}>
              <ServerCard
                server={server}
                isSelected={selectedServers.includes(server.id)}
                isDeleting={deletingNodes.has(server.id)}
                hasPendingOperation={pendingOperations.has(server.id)}
                onSelect={handleServerSelect}
                onDelete={handleDeleteServer}
                onRefresh={handleRefresh}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ 
          backgroundColor: '#1a1a1a', 
          borderRadius: '12px', 
          overflow: 'auto',
          '& .MuiTable-root': {
            minWidth: { xs: '800px', sm: 'auto' }
          }
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedServers(filteredServers.map(server => server.id));
                      } else {
                        setSelectedServers([]);
                      }
                    }}
                    checked={selectedServers.length === filteredServers.length && filteredServers.length > 0}
                    indeterminate={selectedServers.length > 0 && selectedServers.length < filteredServers.length}
                  />
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Hostname</TableCell>
                <TableCell>Region</TableCell>
                <Hidden smDown>
                  <TableCell>Machine ID</TableCell>
                </Hidden>
                <TableCell>IP Address</TableCell>
                <Hidden smDown>
                  <TableCell>Port</TableCell>
                </Hidden>
                <TableCell>Type</TableCell>
                <Hidden mdDown>
                  <TableCell>OS</TableCell>
                  <TableCell>GPU Type</TableCell>
                  <TableCell>GPU Vendor</TableCell>
                  <TableCell>GPU Count</TableCell>
                  <TableCell>VRAM (GB)</TableCell>
                  <TableCell>Function</TableCell>
                  <TableCell>Health</TableCell>
                </Hidden>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServers.map((server) => (
                <ServerTableRow
                  key={server.id}
                  server={server}
                  isSelected={selectedServers.includes(server.id)}
                  isDeleting={deletingNodes.has(server.id)}
                  hasPendingOperation={pendingOperations.has(server.id)}
                  onSelect={handleServerSelect}
                  onDelete={handleDeleteServer}
                  onRefresh={handleRefresh}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddNodeForm
        open={showAddNodeForm}
        onClose={() => setShowAddNodeForm(false)}
        onSuccess={handleAddNodeSuccess}
      />
    </Box>
  );
}
